import type { App as A } from 'vue'

import { registerAccessDirective } from '@vben/access'
import { registerLoadingDirective, setDefaultModalProps } from '@vben/common-ui'
import { preferences } from '@vben/preferences'
import { initStores } from '@vben/stores'
import '@vben/styles'
import '@vben/styles/antd'

import { useTitle } from '@vueuse/core'

import { $t, setupI18n } from '#/locales'

import { setupAdapter } from './adapter'
import App from './app.vue'
import { isInMicroApp, setupMicroApp } from './micro-app'
import { router, setupRouter } from './router'
import { setupSentry } from './setup'
import { setupGtag } from './utils/analytics/gtag-client'

import './style/index.css'

export let app: A<Element> | null = null

export async function bootstrap(namespace: string) {
  setupAdapter()

  setDefaultModalProps({
    fullscreenButton: false,
    destroyOnClose: true,
    centered: true,
    closeOnClickModal: false,
    contentClass: 'p-6',
  })
  // 设置抽屉的默认配置

  app = createApp(App)

  // 注册v-loading指令
  registerLoadingDirective(app, {
    loading: 'loading', // 在这里可以自定义指令名称，也可以明确提供false表示不注册这个指令
    spinning: 'spinning',
  })

  // 国际化 i18n 配置
  await setupI18n(app)

  // 配置 pinia-tore
  await initStores(app, { namespace })

  // 安装权限指令
  registerAccessDirective(app)

  // 初始化 tippy
  const { initTippy } = await import('@vben/common-ui/es/tippy')
  initTippy(app)

  // 配置路由及路由守卫
  setupRouter(app)
  setupSentry(app)

  setupGtag(app, router)

  if (isInMicroApp()) {
    setupMicroApp(app, router)
  }

  // 配置Motion插件
  const { MotionPlugin } = await import('@vben/plugins/motion')
  app.use(MotionPlugin)

  if (!isInMicroApp()) {
    // 动态更新标题
    watchEffect(() => {
      if (preferences.app.dynamicTitle) {
        const routeTitle = router.currentRoute.value.meta?.title
        const pageTitle =
          (routeTitle ? `${$t(routeTitle)} - ` : '') + preferences.app.name
        useTitle(pageTitle)
      }
    })
  }

  app.mount('#app')
}

export function destroyApp() {
  app?.unmount()
  app = null
}
